import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { apiClient } from '@/lib/api-client';
import { HelpCircle, ChevronRight, MessageSquare, Search } from 'lucide-react';
import { Input } from './ui/input';

interface Faq {
    _id: string;
    question: string;
    answer: string;
    category: string;
}

export function FaqsPage() {
    const [faqs, setFaqs] = useState<Faq[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('All');

    useEffect(() => {
        const fetchFaqs = async () => {
            try {
                const data = await apiClient.getFaqs();
                setFaqs(data || []);
            } catch (error) {
                console.error('Failed to fetch FAQs:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchFaqs();
    }, []);

    const categories = ['All', ...Array.from(new Set(faqs.map(f => f.category)))];

    const filteredFaqs = faqs.filter(faq => {
        const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = activeCategory === 'All' || faq.category === activeCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="min-h-screen bg-gray-50 pb-16">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <HelpCircle className="w-10 h-10 opacity-80" />
                        <h1 className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</h1>
                    </div>
                    <p className="text-lg max-w-3xl mx-auto text-blue-100 mb-8">
                        Find answers to common questions about accounts, job applications, employer services, and more.
                    </p>

                    <div className="max-w-2xl mx-auto relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <Input
                            type="text"
                            placeholder="Search for answers..."
                            className="w-full h-12 pl-12 pr-4 text-gray-900 rounded-xl border-none shadow-lg focus:ring-2 focus:ring-blue-400"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
                {/* Category Filters */}
                <div className="flex flex-wrap justify-center gap-3 mb-12">
                    {categories.map((cat) => (
                        <Button
                            key={cat}
                            variant={activeCategory === cat ? 'default' : 'outline'}
                            className={`rounded-full px-8 transition-all duration-200 ${activeCategory === cat
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white text-blue-600 border-blue-200 hover:border-blue-400 hover:bg-blue-50'
                                }`}
                            onClick={() => setActiveCategory(cat)}
                        >
                            {cat}
                        </Button>
                    ))}
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : filteredFaqs.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filteredFaqs.map((faq) => (
                            <Card key={faq._id} className="hover:shadow-lg transition-all duration-300 border-none shadow-sm h-full group">
                                <CardHeader className="pb-3">
                                    <div className="flex justify-between items-start gap-4">
                                        <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                                            {faq.question}
                                        </CardTitle>
                                        <Badge className="bg-blue-100 text-blue-700 font-extrabold uppercase text-[10px] px-2 py-1 rounded border-none whitespace-nowrap">
                                            {faq.category}
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className={`text-gray-600 leading-relaxed ${(faq as any).styling?.italic ? 'italic' : ''} ${(faq as any).styling?.bold ? 'font-bold' : ''} ${!(faq as any).styling ? 'italic' : ''}`}>
                                        {faq.answer}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
                        <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No answers found</h3>
                        <p className="text-gray-600">
                            Try adjusting your search or category filters, or contact our support team.
                        </p>
                    </div>
                )}

                {/* Contact CTA */}
                <section className="mt-20 bg-blue-600 rounded-2xl p-8 md:p-12 text-center text-white shadow-xl">
                    <h2 className="text-3xl font-bold mb-4">Still have questions?</h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                        Our support team is available from 9 AM to 6 PM (Sun-Fri) to help you with any issues you might have.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Button
                            size="lg"
                            className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8"
                            onClick={() => window.location.href = '/contact'}
                        >
                            Contact Support
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            className="border-white text-white hover:bg-white/10 font-semibold px-8"
                        >
                            Live Chat
                        </Button>
                    </div>
                </section>
            </div>
        </div>
    );
}
